import { AttachFile, Close, Send } from '@mui/icons-material';
import { IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { Column, Row } from 'further-ui/components/Layout';

const InputSection = styled('div')(({ theme }) => ({
  padding: theme.spacing(2, 5, 5),
  flexShrink: 0,
}));

const InputWrapper = styled('div', {
  shouldForwardProp: (p) => p !== 'isDragging',
})<{ isDragging: boolean }>(({ theme, isDragging }) => ({
  backgroundColor: isDragging
    ? `${theme.palette.primary.main}12`
    : theme.palette.white,
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(4),
  transition: 'background-color 0.15s',
  cursor: 'text',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  padding: `0 ${theme.spacing(2)}`,

  '& .MuiInputBase-input::placeholder': {
    color: theme.palette.grey[500],
    opacity: 1,
  },
}));

const FileCardRow = styled(Row)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const FileCard = styled(Column)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  padding: theme.spacing(2),
  width: 96,
  height: 84,
  overflow: 'hidden',
}));

const FileCardFileType = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

const FileCardName = styled(Typography)(({ theme }) => ({
  display: '-webkit-box',
  // the below ensures that 2 lines of text are shown before the ellipsis kicks in
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: '125%',
  fontWeight: theme.typography.fontWeightMedium,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}));

const SendButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[800],
  width: 32,
  height: 32,
  flexShrink: 0,
  '&:hover': { backgroundColor: theme.palette.grey[900] },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[300],
  },
  '& svg': {
    color: theme.palette.common.white,
  },
  '&.Mui-disabled svg': {
    color: theme.palette.grey[500],
  },
}));

const getFileTypeLabel = (file: File): string => {
  if (
    file.type === 'application/pdf' ||
    file.name.toLowerCase().endsWith('.pdf')
  )
    return 'PDF';
  const ext = file.name.split('.').pop();
  return ext ? ext.toUpperCase() : 'FILE';
};

type Props = {
  inputText: string;
  onInputChange: (text: string) => void;
  onSend: (text: string) => void;
  isLoading: boolean;
  stagedFile: File | null;
  onClearFile: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  isDragging: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAttachClick: () => void;
};

const ChatInput = ({
  inputText,
  onInputChange,
  onSend,
  isLoading,
  stagedFile,
  onClearFile,
  fileInputRef,
  inputRef,
  isDragging,
  onFileChange,
  onAttachClick,
}: Props) => (
  <InputSection>
    <input
      ref={fileInputRef}
      type="file"
      accept="application/pdf"
      style={{ display: 'none' }}
      onChange={onFileChange}
    />
    <InputWrapper
      isDragging={isDragging}
      onClick={() => inputRef.current?.focus({ preventScroll: true })}
    >
      {stagedFile && (
        <FileCardRow spacing="sm">
          <FileCard justify="space-between" spacing="xs">
            <Row centered spacing="xs" justify="space-between">
              <FileCardFileType variant="body2" color="grey.500">
                {getFileTypeLabel(stagedFile)}
              </FileCardFileType>
              <StyledIconButton
                size="small"
                onClick={onClearFile}
                disabled={isLoading}
              >
                <Close sx={{ fontSize: 14 }} />
              </StyledIconButton>
            </Row>
            <FileCardName variant="body2" color="grey.900">
              {stagedFile.name}
            </FileCardName>
          </FileCard>
        </FileCardRow>
      )}
      <StyledInputBase
        multiline
        maxRows={5}
        fullWidth
        placeholder={isDragging ? 'Drop file here…' : 'Chat with AI Agent'}
        value={inputText}
        onChange={(e) => onInputChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSend(inputText);
          }
        }}
        disabled={isLoading}
        inputRef={inputRef}
      />
      <Row centered justify="space-between">
        <Tooltip title="Attach file">
          <IconButton size="small" onClick={onAttachClick} disabled={isLoading}>
            <AttachFile fontSize="small" sx={{ color: 'text.secondary' }} />
          </IconButton>
        </Tooltip>
        <SendButton
          onClick={() => onSend(inputText)}
          disabled={isLoading || (!inputText.trim() && !stagedFile)}
        >
          <Send sx={{ fontSize: 16 }} />
        </SendButton>
      </Row>
    </InputWrapper>
  </InputSection>
);

export default ChatInput;
